import { AppNode, EdgeData } from "@features/builder/graph/types"
import { LinkedGraphConfig } from "@features/builder/types"
import { GraphDto } from "@custom-types/graphs"
import { graphAtom, maxLagAtom } from "@features/run/RunGroupDialog/atoms"
import { getMaxLag } from "@features/run/RunGroupDialog/rolling-date"
import { RunGroupFormValues } from "@features/run/RunGroupDialog/RunGroupForm"
import { useLinkedRunValidation } from "@features/run/RunGroupDialog/RunGroupForm/useLinkedRunValidation"
import { useAllLinkedGraphValidations } from "@features/run/RunGroupDialog/RunGroupForm/useAllLinkedGraphValidations"
import { LinkedGraphValidField } from "@features/run/RunGroupDialog/RunGroupForm/LinkedGraphValidField"
import { useHasValidRunsForGraph } from "@features/run/RunGroupDialog/RunGroupForm/useHasValidRunsForGraph"
import { linkEncode, linkParse } from "@components/NodeLink/linkHelpers"
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Edge, useReactFlow } from "@xyflow/react"
import { useAtomValue, useSetAtom } from "jotai"
import { useEffect, useMemo } from "react"
import { Controller, useFieldArray, useFormContext } from "react-hook-form"
import { getErrorMessage } from "utils/form"
import { OpenInNew } from "@mui/icons-material"

const samplingModes: LinkedGraphConfig["linked_graph_run_mode"][] = [
  "MERGE",
  "PARALLEL",
  "PREVIOUS",
]
/**
 * @warning
 * This component is compatible with the graph data model used on the BUILD PAGE.
 * It uses a new version of react-flow (@xyflow/react) and a different graph data model.
 * It will not work on the SCENARIO DESIGN PAGE.
 * If you need it on the scenario design page, you need to fork it.
 */
export const LinkedGraphMode = () => {
  const graph = useAtomValue(graphAtom)
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<RunGroupFormValues>()

  const { fields, replace } = useFieldArray({
    control,
    name: "previous_run_key",
  })

  const { getNodes } = useReactFlow<AppNode, Edge<EdgeData>>()

  const graphLinks = useMemo(() => {
    const graphLinks: string[] = []
    for (const node of getNodes()) {
      if (!("metaNode" in node.data)) continue
      if (!node.data.metaNode.Link?.trim()) continue

      graphLinks.push(node.data.metaNode.Link)
    }
    return graphLinks
  }, [getNodes])

  const maxLag = useMemo(() => getMaxLag(getNodes()), [getNodes])
  const setMaxLag = useSetAtom(maxLagAtom)

  useEffect(() => {
    setMaxLag(maxLag)
  }, [maxLag, setMaxLag])

  useEffect(() => {
    const uniqueGraphKeys = new Set(
      graphLinks
        .map((link) => {
          const parsed = linkParse(link)
          if (!parsed) return undefined

          return linkEncode(parsed.org, parsed.div, parsed.prod, parsed.family, parsed.version)
        })
        .filter((graphKey): graphKey is string => Boolean(graphKey)),
    )

    const runKeysFieldsValues = []
    for (const link of uniqueGraphKeys) {
      runKeysFieldsValues.push({ graphKey: link, runId: "" })
    }
    replace(runKeysFieldsValues)
  }, [graphLinks, replace])

  const linkedGraphKeys = useMemo(() => fields.map((f) => f.graphKey), [fields])

  if (!graph || !graphLinks.length) return <></>

  return (
    <>
      <Typography variant={"h6"}>Linked graph mode</Typography>
      <Controller
        control={control}
        name="linked_graph_mode"
        render={({ field }) => (
          <FormControl error={Boolean(errors[field.name])}>
            <Select {...field} value={field.value || "MERGE"}>
              {samplingModes.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {Boolean(errors[field.name]) && (
              <FormHelperText>{getErrorMessage(errors, field.name)}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      {(watch("linked_graph_mode") === "PARALLEL" || watch("linked_graph_mode") === "MERGE") && (
        <LinkedGraphErrors graphKeys={linkedGraphKeys} />
      )}

      {watch("linked_graph_mode") === "PREVIOUS" && (
        <>
          {fields.map((field, index) => (
            <Controller
              key={field.id}
              name={`previous_run_key.${index}.runId`}
              control={control}
              render={({ field: controllerField, fieldState }) => (
                <PreviousRunIdField
                  field={controllerField}
                  fieldState={fieldState}
                  graphKey={field.graphKey}
                  graph={graph}
                  maxLag={maxLag}
                />
              )}
            />
          ))}
        </>
      )}
    </>
  )
}

function PreviousRunIdField({
  field,
  fieldState,
  graphKey,
  graph,
  maxLag,
}: {
  field: { value: string; onChange: (value: string) => void; onBlur: () => void; name: string }
  fieldState: { invalid: boolean; isValidating: boolean }
  graphKey?: string
  graph: GraphDto
  maxLag: number
}) {
  const fieldName = field.name as `previous_run_key.${number}.runId`
  const { errors, isValidating, validate, validateImmediate } = useLinkedRunValidation({
    fieldName,
    graphKey,
    graph,
    maxLag,
  })
  const { isLoading: isCheckingRuns, hasValidRuns } = useHasValidRunsForGraph(graphKey)

  const { watch: watchForm } = useFormContext<RunGroupFormValues>()
  const numberOfSamplings = watchForm("number_of_samplings")
  const endDate = watchForm("end_date")

  useEffect(() => {
    if (field.value) {
      validateImmediate(field.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- force revalidation when those fields will changed
  }, [numberOfSamplings, endDate])

  const handleChange = (value: string) => {
    field.onChange(value)
    validate(value)
  }

  const handleBlur = () => {
    field.onBlur()
    validateImmediate(field.value)
  }

  const hasError = fieldState.invalid || errors.length > 0

  return (
    <Grid display="flex" alignItems="flex-start">
      <span style={{ flex: 1 }}>
        <LinkedGraphValidField
          isValidating={isValidating || isCheckingRuns}
          haveErrors={errors.length > 0 || !hasValidRuns}
        />
        <TextField
          error={hasError}
          label={`Group ID for: ${graphKey}`}
          title={`Group ID for: ${graphKey}`}
          variant="standard"
          name={field.name}
          value={field.value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          fullWidth
          helperText={isValidating || isCheckingRuns ? "Validating..." : undefined}
          disabled={!hasValidRuns}
        />
        {!isValidating &&
          errors.map((err, idx) => (
            <FormHelperText key={idx} error>
              - {err}
            </FormHelperText>
          ))}
        {!isCheckingRuns && !hasValidRuns && (
          <FormHelperText error>- No valid runs available for this graph</FormHelperText>
        )}
      </span>

      <Tooltip title="View Runs">
        <span>
          <IconButton
            size="small"
            component={Link}
            href={
              graphKey
                ? `/ScenarioDesign?graphKey=${encodeURIComponent(graphKey)}&mode=runs`
                : undefined
            }
            disabled={!graphKey || !hasValidRuns}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Grid>
  )
}

function LinkedGraphErrors({ graphKeys }: { graphKeys: string[] }) {
  const { isValidating, errors } = useAllLinkedGraphValidations(graphKeys)

  return (
    <Grid display="flex" alignItems="flex-start">
      <span style={{ flex: 1 }}>
        <LinkedGraphValidField isValidating={isValidating} haveErrors={errors.length > 0} />
        {isValidating && <FormHelperText>Validating graph...</FormHelperText>}
        {!isValidating &&
          errors.map((err, idx) => (
            <FormHelperText key={idx} error>
              - {err}
            </FormHelperText>
          ))}
      </span>
    </Grid>
  )
}
